import get from 'lodash-es/get';
import forEach from 'lodash-es/forEach';
import sortBy from 'lodash-es/sortBy';
import isEmpty from 'lodash-es/isEmpty';
import isEqual from 'lodash-es/isEqual';
import find from 'lodash-es/find.js';
import * as router from '../router';
import * as card from '../card';
import * as board from '../board';
import * as auth from '../auth';
// Direct path — ai-chat/index.js has saga/reducer side-effects that TDZ-throw
// when pulled in too early. We only need selectors here.
import * as aiChatSelectors from '../ai-chat/selectors.js';
// Direct path for the same reason — card-translate/index.js pulls in its saga.
import * as cardTranslateSelectors from '../card-translate/selectors.js';
import * as firestoreRedux from '@dreamworld/firestore-redux';
import * as boardTagsSelectors from '../board-tags/selectors.js';
import Permission from '../board/permission';
import { createSelector } from 'reselect';
import { toEditorValue } from '../references/utils.js';
import { titleOf } from '../references/anchors.js';

/**
 * @params
 *  @property {Object} state Redux state
 *  @property {String} cardId Card Id.
 * @returns {Object} Permission instance.
 */
export const cardEffectivePermission = createSelector(
  card.selectors.attrs,
  (state, cardId, boardId) => board.selectors.boardEffectivePermission(state, boardId),
  (state, cardId, boardId) => {
    // While an AI suggestion (NEW / EDIT / DELETE) is WAITING on this card,
    // every mutating interaction is suppressed — the user must approve or
    // reject the proposal first. We compute the suggestion type here so the
    // selector recomputes whenever it flips.
    const userId = auth.selectors.currentUserId(state);
    const chatId = aiChatSelectors.pendingEditChatId(state, boardId, cardId, userId);
    if (!chatId) return null;
    return aiChatSelectors.cardSuggestionType(state, chatId, cardId) || null;
  },
  (state, cardId) => cardTranslateSelectors.translatedLanguage(state, cardId) || null,
  (cardDetail, boardEffectivePermission, aiSuggestionType, translatedLanguage) => {
    if (isEmpty(cardDetail)) {
      return new Permission('NONE');
    }

    //If board effective permission is not NONE and card is in Done or Trash column
    if (cardDetail.columnType === 'DONE' || cardDetail.columnType === 'TRASH') {
      return new Permission('READ');
    }

    // Any pending AI suggestion → force read-only until approve/reject.
    if (aiSuggestionType) {
      return new Permission('READ');
    }

    // Showing a translation → force read-only until Show original. An editor fills itself
    // from what the screen renders, so saving would write the translation back over the
    // author's text for everyone.
    if (translatedLanguage) {
      return new Permission('READ');
    }

    return new Permission(boardEffectivePermission.permission);
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 3000,
    },
  }
);

/**
 * Effective permission for card-scoped CHAT.
 *
 * Same as the board's effective permission, but forced READ-only while an AI
 * suggestion (NEW / EDIT / DELETE) is WAITING on the card, or while the card is
 * showing a translation. Unlike
 * `cardEffectivePermission`, it does NOT downgrade DONE / TRASH cards — chat may
 * still be added to done and deleted cards.
 *
 * @params
 *  @property {Object} state Redux state
 *  @property {String} cardId Card Id.
 *  @property {String} boardId Board Id.
 * @returns {Object} Permission instance.
 */
export const cardChatEffectivePermission = createSelector(
  card.selectors.attrs,
  (state, cardId, boardId) => board.selectors.boardEffectivePermission(state, boardId),
  (state, cardId, boardId) => {
    const chatId = aiChatSelectors.pendingEditChatId(state, boardId, cardId);
    if (!chatId) return null;
    return aiChatSelectors.cardSuggestionType(state, chatId, cardId) || null;
  },
  (state, cardId, boardId) => {
    const chatId = aiChatSelectors.pendingEditChatId(state, boardId, cardId);
    if (!chatId) return false;
    return aiChatSelectors.isOnlyChatProposalsPending(state, chatId, cardId);
  },
  (state, cardId) => cardTranslateSelectors.translatedLanguage(state, cardId) || null,
  (cardDetail, boardEffectivePermission, aiSuggestionType, isOnlyChatProposalsPending, translatedLanguage) => {
    if (isEmpty(cardDetail)) {
      return new Permission('NONE');
    }

    // Any pending AI suggestion → force read-only until approve/reject.
    // Note: no DONE / TRASH downgrade — chat is allowed on done/deleted cards.
    // The exception is a chat proposal and nothing else — a message being written or one being
    // rewritten. Both are shown in an editor for the user to correct, so locking the thread would
    // take away the point of them.
    if (aiSuggestionType && !isOnlyChatProposalsPending) {
      return new Permission('READ');
    }

    // Showing a translation → a chat message must not be editable into its translation.
    if (translatedLanguage) {
      return new Permission('READ');
    }

    return new Permission(boardEffectivePermission.permission);
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 3000,
    },
  }
);

/**
 * @param { Number } cardId - Id of card which tagsId wants to compute
 * @param { Number } boardId - Id of board of card
 * @return { Array } - card assigned tags Ids.
 */
export const assignedTags = createSelector(
  (state, cardId) => card.selectors.summary(state, cardId),
  (summary) => get(summary, 'tags', []),
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 3000,
    },
  }
);

/**
 * @param
 *  @property { Object } state - Redux state
 *  @property { String } boardId - Id of board of card
 * @return { Array } - board tags.
 */
export const boardTagModels = createSelector(
  (state, boardId) => firestoreRedux.selectors.docsByQueryResult(state, `board-tags-${boardId || router.selectors.dialogBoardId(state)}`, 'board-tags'),
  (state) => boardTagsSelectors.allowedColors(state),
  (boardTags, allowedColors) => {
    let list = [];
    forEach(boardTags, (tag) => {
      const color = get(allowedColors, `${tag.color}.value`, '');
      list.push({ tagId: tag.id, tag: { ...tag, color }, name: tag.name });
    });

    return sortBy(list, ['name']);
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 50,
    },
  }
);

/**
 * AI-proposed tags for a card, resolved to `[{ id, name, color }]` (the same resolution as a
 * card's committed tags), or null when no `updateCardTags` proposal is pending. Lets the board
 * card show the proposed set without touching the committed `cards-summary.tags` the diff reads
 * as its "before". Resolves against the `board-tags` collection (like
 * `card.selectors.cardAssignedTags`), so it works on the board and in the aggregated views alike
 * — unlike the board-scoped `board-tags-<boardId>` query the views don't run. Lives here (not in
 * `redux/card`) because it needs `ai-chat` selectors, and `card/selectors` is in the store-init
 * graph — importing `ai-chat` there closes a circular dependency (store TDZ at load).
 */
export const proposedCardTags = createSelector(
  (state, chatId, cardId) => (chatId && cardId ? aiChatSelectors.toolCallForField(state, chatId, cardId, 'tags') : null),
  (state) => firestoreRedux.selectors.collection(state, 'board-tags'),
  (state) => boardTagsSelectors.allowedColors(state),
  (toolCall, boardTags, allowedColors) => {
    if (!toolCall) return null;
    const list = [];
    forEach(toolCall.input?.tags || [], (id) => {
      const name = get(boardTags, `${id}.name`);
      const colorId = get(boardTags, `${id}.color`);
      const color = get(allowedColors, `${colorId}.value`, '');
      list.push({ id, name, color });
    });
    return sortBy(list, ['name']);
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 500,
    },
  }
);

/**
 * @param
 *  @property {Object} state Redux state.
 *  @property {String} cardId Card Id.
 * @return { Array } - card assigned assignments Ids.
 */
/**
 * @param
 *  @property {Object} state Redux state.
 *  @property {String} cardId Card Id.
 * @return { Array } - card assigned assignments Ids.
 */
export const assignments = createSelector(
  (state, cardId) => firestoreRedux.selectors.doc(state, 'cards', cardId),
  (doc) => get(doc, 'assignments', []),
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 3000,
    },
  }
);


const _editorDescriptions = new Map();

/**
 * What the description editor loads: `descriptionHtml`, else the old `description` converted with the titles
 * loaded now. Recomputed only when the stored description changes, so a title that loads later never looks
 * like someone else's edit; the chips show the live title themselves.
 * @returns {String|undefined} undefined while the card is not loaded
 */
export const editorDescription = (state, cardId) => {
  const doc = firestoreRedux.selectors.doc(state, 'cards', cardId);
  if (!doc) {
    return undefined;
  }
  const { descriptionHtml, description, description2 } = doc;
  const cached = _editorDescriptions.get(cardId);
  if (cached && cached.descriptionHtml === descriptionHtml && cached.description === description) {
    return cached.value;
  }
  const value = toEditorValue({ descriptionHtml, description }, { titleOf: titleOf(state), isOurUrl: router.actions.isAppUrl, oldHtml: description2 });
  _editorDescriptions.set(cardId, { descriptionHtml, description, value });
  return value;
};

/**
 * @returns {Boolean} currently opened is deleted or not.
 */
export const isDeleted = createSelector(
  (state, cardId) => firestoreRedux.selectors.doc(state, 'cards', cardId || router.selectors.cardId(state)),
  (state, cardId) => get(state, `cardDetails.openCardStatus.${cardId || router.selectors.cardId(state)}.deleted`),
  (doc, deleted) => {
    if(!isEmpty(doc)) {
      return false;
    }

    return !!deleted;
  },
  {
    memoizeOptions: {
      maxSize: 500,
    },
  }
);

/**
 * @returns {Boolean} currently opened card data is loaded or not.
 */
export const loaded = createSelector(
  (state, cardId) => firestoreRedux.selectors.doc(state, 'cards', cardId || router.selectors.cardId(state)),
  (state, cardId) => get(state, `cardDetails.openCardStatus.${cardId || router.selectors.cardId(state)}.loaded`),
  (doc, loaded) => {
    if(!isEmpty(doc)) {
      return true;
    }

    return !!loaded;
  },
);

/**
 * @param {Object} state Redux state.
 * @param {String} cardId Id of the card.
 * @returns {String} What the user has typed into the description editor and not saved yet.
 */
export const descriptionDraft = (state, cardId) => get(state, `cardDetails.descriptionDrafts.${cardId}.description`, '');

/**
 * The description the unsaved edit is working against, captured when it was written.
 *
 * Kept beside the draft so the someone-else-changed-this check still has the value the user started
 * from after a refresh — reading the current description instead would hide every remote change.
 *
 * @param {Object} state Redux state.
 * @param {String} cardId Id of the card.
 * @returns {String} The saved description the draft was written against.
 */
export const descriptionDraftOriginal = (state, cardId) => get(state, `cardDetails.descriptionDrafts.${cardId}.originalDescription`, '');

/**
 * Whether an AI rewrite of this card's description may be corrected in its editor: the card's own
 * write permission without the AI lock — Done / Trash and a translated view stay read-only.
 *
 * @param {Object} state Redux state.
 * @param {String} cardId Id of the card.
 * @param {String} boardId Id of the card's board.
 * @returns {Boolean}
 */
export const canEditDescriptionProposal = (state, cardId, boardId) => {
  const columnType = get(card.selectors.attrs(state, cardId), 'columnType');
  if (columnType === 'DONE' || columnType === 'TRASH' || cardTranslateSelectors.translatedLanguage(state, cardId)) {
    return false;
  }

  const permission = board.selectors.boardEffectivePermission(state, boardId);
  return !!(permission && permission.hasWrite());
};
